/**
 * PageNotesContext — one-shot `/me/can-edit-page-notes` fetch, cached.
 *
 * Provides `useCanEditPageNotes()`. The boolean gates authoring and editorial
 * history. The marker separately checks whether a read-only user has an
 * active note to view.
 *
 * Mount inside <AuthProvider> in App.tsx so the fetch fires only after a
 * session exists. Boots silently — the initial state is `ready:false,
 * canEdit:false`, and the fetch result resolves once.
 *
 * Error handling distinguishes definite-no (401/403) from transient
 * failures (network blip, 5xx). A definite-no resolves immediately as
 * non-staff; a transient failure schedules one retry before falling back
 * to the non-staff state. Without this distinction, a single network blip
 * during the silent-refresh window would flag a staff user as non-staff
 * until next reload — see code-review #10.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from './AuthContext';
import { ApiResponseError } from '../lib/api';
import { pageNotesApi } from '../lib/pageNotes';

interface PageNotesContextValue {
  /** True iff the backend says this user is in `staff_users`. */
  canEdit: boolean;
  /** False until the `/me/can-edit-page-notes` fetch settles. */
  ready: boolean;
}

const PageNotesContext = createContext<PageNotesContextValue | undefined>(
  undefined,
);

interface PageNotesProviderProps {
  children: ReactNode;
}

// Single retry after this delay for transient failures (network blip,
// 5xx, or a 401 that survived api.ts's silent-refresh attempt).
const RETRY_DELAY_MS = 3000;

export function PageNotesProvider({ children }: PageNotesProviderProps) {
  const { userEmail } = useAuth();
  const [canEdit, setCanEdit] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;

    if (!userEmail) {
      setCanEdit(false);
      setReady(false);
      return;
    }

    const fetchOnce = async (attempt: number): Promise<void> => {
      try {
        const { can_edit } = await pageNotesApi.canEdit();
        if (cancelled) return;
        setCanEdit(Boolean(can_edit));
        setReady(true);
      } catch (e: unknown) {
        if (cancelled) return;
        const status =
          e instanceof ApiResponseError ? e.status : undefined;
        // 401 / 403 are definite-no answers — no point retrying.
        if (status === 401 || status === 403) {
          setCanEdit(false);
          setReady(true);
          return;
        }
        // Transient failure — retry once. After that, fall through to
        // the non-staff default (the next user-driven request will
        // benefit from api.ts's silent refresh path).
        if (attempt < 1) {
          timeoutId = window.setTimeout(() => {
            if (!cancelled) void fetchOnce(attempt + 1);
          }, RETRY_DELAY_MS);
          return;
        }
        setCanEdit(false);
        setReady(true);
      }
    };

    setReady(false);
    void fetchOnce(0);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [userEmail]);

  return (
    <PageNotesContext.Provider value={{ canEdit, ready }}>
      {children}
    </PageNotesContext.Provider>
  );
}

export function useCanEditPageNotes(): PageNotesContextValue {
  const ctx = useContext(PageNotesContext);
  if (!ctx) {
    // Defensive default: if a marker mounts outside the provider
    // (shouldn't happen in app code, but might in isolated tests),
    // behave as non-staff rather than throwing.
    return { canEdit: false, ready: true };
  }
  return ctx;
}
